import { createAsyncThunk, createSlice, PayloadAction } from "@reduxjs/toolkit";
import { State } from "@features/base/types";
import {
  getMerchantPayoutOrdersApi,
  MerchantOrderPayout,
  MerchantOrderPayoutRequest,
  OrderPayoutResponse,
} from "./api";
import {
  createAppealApi,
  CreateAppealRequest,
  uploadReceiptApi,
  UploadReceiptRespnose,
} from "@features/appeals/api";

export const getMerchantPayoutOrders = createAsyncThunk<
  OrderPayoutResponse,
  { data: MerchantOrderPayoutRequest }
>(
  "merchantPayouts/getMerchantPayoutOrdersApi",
  async ({ data }, { rejectWithValue }) => {
    try {
      return await getMerchantPayoutOrdersApi(data);
    } catch (error) {
      return rejectWithValue(error);
    }
  }
);

export const uploadPayOutReceipt = createAsyncThunk<
  UploadReceiptRespnose,
  { orderId: string; file: FileList }
>(
  "merchantPayouts/uploadReceipt",
  async ({ orderId, file }, { rejectWithValue }) => {
    try {
      return await uploadReceiptApi(orderId, file);
    } catch (error) {
      return rejectWithValue(error);
    }
  }
);

export const createAppeal = createAsyncThunk<
  unknown,
  { data: CreateAppealRequest }
>("merchantPayouts/createAppeal", async ({ data }, { rejectWithValue }) => {
  try {
    return await createAppealApi(data);
  } catch (error) {
    return rejectWithValue(error);
  }
});

type PayoutOrdersState = State<{
  payoutOrders: MerchantOrderPayout[] | null;
  page: number;
  totalCount: number;
  payoutOrdersLoading: boolean;
  isReceiptLoading: boolean;
  receiptUrls: string[];
  message: string;
  isAppealLoading: boolean;
}>;

const initialState: PayoutOrdersState = {
  payoutOrders: null,
  loading: false,
  payoutOrdersLoading: false,
  error: null,
  page: 1,
  totalCount: 0,
  isReceiptLoading: false,
  receiptUrls: [],
  message: "",
  isAppealLoading: false,
};

const merchantPayoutOrdersSlice = createSlice({
  name: "merchantPayouts",
  initialState,
  reducers: {
    setMessage: (state, action: PayloadAction<string>) => {
      state.message = action.payload;
    },
    resetAppeal: (state) => {
      state.receiptUrls = [];
      state.message = "";
    },
    setReceipts: (state, action: PayloadAction<string[]>) => {
      state.receiptUrls = action.payload;
    },
    removeFile: (state, action: PayloadAction<string>) => {
      state.receiptUrls = state.receiptUrls.filter(
        (url) => url !== action.payload
      );
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(getMerchantPayoutOrders.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(getMerchantPayoutOrders.fulfilled, (state, action) => {
        state.loading = false;
        state.payoutOrders = action.payload.pay_out_orders;
        state.page = action.payload.page;
        state.totalCount = action.payload.count;
      })
      .addCase(getMerchantPayoutOrders.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      })
      .addCase(uploadPayOutReceipt.pending, (state) => {
        state.isReceiptLoading = true;
      })
      .addCase(uploadPayOutReceipt.fulfilled, (state, action) => {
        state.isReceiptLoading = false;
        if (action.payload.urls != null)
          state.receiptUrls = [...state.receiptUrls, ...action.payload.urls];
      })
      .addCase(uploadPayOutReceipt.rejected, (state) => {
        state.isReceiptLoading = false;
      })
      .addCase(createAppeal.pending, (state, action) => {
        state.isAppealLoading = true;
        console.log(action.payload);
      })
      .addCase(createAppeal.fulfilled, (state, action) => {
        state.isAppealLoading = false;
        console.log(action.payload);
      })
      .addCase(createAppeal.rejected, (state, action) => {
        state.isAppealLoading = false;
        state.error = action.payload as string;
      });
  },
});

export default merchantPayoutOrdersSlice.reducer;
export const { setReceipts, setMessage, resetAppeal, removeFile } =
  merchantPayoutOrdersSlice.actions;
